import type { FC, ReactNode } from 'react'
import type { ConsortiumCategory } from '@/types'

import { Check } from 'lucide-react'
import { SectionHeading } from '@/components/landing/section-heading'

interface CategoryUsesSectionProps {
    category: ConsortiumCategory
}

export const CategoryUsesSection: FC<CategoryUsesSectionProps> = ({ category }): ReactNode => {
    return (
        <section
            id='o-que-comprar'
            aria-labelledby='o-que-comprar-titulo'
            className='border-y border-border bg-card/60 py-24 sm:py-32'
        >
            <div className='mx-auto grid max-w-7xl items-start gap-14 px-5 sm:px-8 lg:grid-cols-[0.9fr_1.1fr] lg:gap-20'>
                <div className='lg:sticky lg:top-28'>
                    <SectionHeading
                        id='o-que-comprar-titulo'
                        eyebrow='Carta de crédito'
                        title='O que você pode fazer com o crédito'
                        description={`No ${category.title.toLowerCase()}, a carta de crédito funciona como dinheiro à vista. Veja as possibilidades mais comuns.`}
                    />

                    <ul className='mt-10 grid gap-6'>
                        {category.highlights.map(highlight => (
                            <li key={highlight.title} className='flex items-start gap-4'>
                                <span className='grid size-10 shrink-0 place-items-center rounded-full bg-gold text-gold-foreground'>
                                    <Check aria-hidden='true' className='size-5' />
                                </span>

                                <div>
                                    <p className='font-bold text-foreground'>
                                        {highlight.title}
                                    </p>

                                    <p className='mt-1 text-sm leading-relaxed text-muted-foreground'>
                                        {highlight.description}
                                    </p>
                                </div>
                            </li>
                        ))}
                    </ul>
                </div>

                <ul className='grid gap-4 sm:grid-cols-2'>
                    {category.uses.map((use, index) => (
                        <li key={use.title} className='rounded-[1.5rem] bg-card p-6 shadow-xs ring-1 ring-border sm:p-7'>
                            <span aria-hidden='true' className='font-heading text-3xl leading-none text-bronze'>
                                {String(index + 1).padStart(2, '0')}
                            </span>

                            <p className='mt-5 text-lg font-bold text-foreground'>
                                {use.title}
                            </p>

                            <p className='mt-1.5 text-sm leading-relaxed text-muted-foreground'>
                                {use.description}
                            </p>
                        </li>
                    ))}
                </ul>
            </div>

            <p className='mx-auto mt-12 max-w-7xl px-5 text-sm text-muted-foreground sm:px-8'>
                O uso da carta de crédito segue as regras da administradora e do contrato do grupo.
            </p>
        </section>
    )
}
